import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../api";
import {
  POLICIES, POLICY_ORDER, getPolicy,
} from "../legal/legalContent";
import {
  Save, Undo2, FileEdit, Eye, AlertCircle, CheckCircle2, Loader2,
  Plus, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

/**
 * Admin editor for the legal/privacy/terms hub.
 *
 *   /admin/legal           — listing of all 10 policies + status (default / custom)
 *   /admin/legal/:slug     — single-policy editor (handled inline via state)
 *
 * Behaviour:
 *   - Form is seeded from the DB override if present, else from the
 *     static defaults in `src/legal/legalContent.js`.
 *   - Save → `PUT /api/admin/legal/policies/:slug` (full upsert).
 *   - Revert → `DELETE /api/admin/legal/policies/:slug` (removes the
 *     override; the public page falls back to defaults).
 */
export default function AdminLegalEditor() {
  usePageTitle("Legal pages · Admin");
  const [overrides, setOverrides] = useState({});  // slug → doc
  const [editing, setEditing] = useState(null);    // slug or null
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), type === "ok" ? 3500 : 6000);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/legal/policies");
      const map = {};
      for (const p of r.data || []) map[p.slug] = p;
      setOverrides(map);
    } catch (e) {
      // ignore — empty state is fine
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (editing) {
    return (
      <PolicyEditor
        slug={editing}
        currentOverride={overrides[editing]}
        onBack={() => setEditing(null)}
        onSaved={(doc) => {
          setOverrides((m) => ({ ...m, [doc.slug]: doc }));
          showNotice("ok", `Saved “${doc.title}”.`);
          setEditing(null);
        }}
        onReverted={() => {
          const next = { ...overrides };
          delete next[editing];
          setOverrides(next);
          showNotice("ok", "Reverted to default content.");
          setEditing(null);
        }}
        onError={(msg) => showNotice("err", msg)}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-legal-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><FileEdit size={12}/> Site content</span>
          <h1 className="font-display font-black text-4xl mt-1">Legal & policy editor</h1>
          <p className="text-muted text-sm mt-1 max-w-xl">
            Edit the content shown at <Link to="/legal" className="text-signal hover:underline">/legal</Link>.
            Each page falls back to the shipped defaults until you publish a custom version here.
          </p>
        </div>
        <Link to="/legal" target="_blank" rel="noopener noreferrer" className="btn-secondary"
              data-testid="admin-legal-open-public">
          <Eye size={12}/> View public hub
        </Link>
      </header>

      {notice && (
        <div className={[
          "rounded-sm border px-3 py-2 text-sm flex items-center gap-2",
          notice.type === "ok"
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-red-50 border-red-200 text-red-700",
        ].join(" ")} data-testid="admin-legal-notice">
          {notice.type === "ok" ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
          {notice.text}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="dense" data-testid="admin-legal-table">
          <thead>
            <tr>
              <th>Policy</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Last edited</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {POLICY_ORDER.map((s) => {
              const def = getPolicy(s);
              const ov = overrides[s];
              return (
                <tr key={s} data-testid={`admin-legal-row-${s}`}>
                  <td>
                    <div className="font-medium">{def?.title}</div>
                    <div className="text-xs text-muted">{def?.eyebrow}</div>
                  </td>
                  <td><code className="text-xs">/legal/{s}</code></td>
                  <td>
                    {ov ? (
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">
                        Custom
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm bg-paper text-muted border border-rule">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="text-xs text-muted">
                    {ov?.updated_at
                      ? new Date(ov.updated_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <button className="btn-ghost" onClick={() => setEditing(s)}
                            data-testid={`admin-legal-edit-${s}`}>
                      <FileEdit size={12}/> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && <div className="p-3 text-muted text-xs"><Loader2 size={12} className="inline animate-spin"/> Loading overrides…</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Single-policy form                                                    */
/* -------------------------------------------------------------------- */

function PolicyEditor({ slug, currentOverride, onBack, onSaved, onReverted, onError }) {
  const staticDef = getPolicy(slug);
  const seed = currentOverride || staticDef;
  const [form, setForm] = useState(() => ({
    title: seed?.title || "",
    eyebrow: seed?.eyebrow || "",
    intro: seed?.intro || "",
    sections: (seed?.sections || []).map((s) => ({
      heading: s.heading || "",
      paras: (s.paras || []).join("\n\n"),
      bullets: (s.bullets || []).join("\n"),
      table: s.table || null,
    })),
  }));
  const [collapsed, setCollapsed] = useState({}); // i → bool
  const [busy, setBusy] = useState(false);

  const setSection = (i, patch) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, j) => j === i ? { ...s, ...patch } : s),
    }));
  };

  const addSection = () => {
    setForm((f) => ({
      ...f,
      sections: [...f.sections, { heading: "New section", paras: "", bullets: "", table: null }],
    }));
  };
  const removeSection = (i) => {
    if (!window.confirm("Remove this section?")) return;
    setForm((f) => ({ ...f, sections: f.sections.filter((_, j) => j !== i) }));
  };
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.sections.length) return;
    const next = [...form.sections];
    [next[i], next[j]] = [next[j], next[i]];
    setForm((f) => ({ ...f, sections: next }));
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    eyebrow: form.eyebrow.trim(),
    intro: form.intro.trim(),
    sections: form.sections.map((s) => ({
      heading: s.heading.trim(),
      paras: s.paras
        .split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
      bullets: s.bullets
        .split(/\n+/).map((b) => b.trim()).filter(Boolean),
      ...(s.table ? { table: s.table } : {}),
    })),
  });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/legal/policies/${slug}`, buildPayload());
      onSaved?.(r.data);
    } catch (e) {
      onError?.(formatError(e) || "Could not save.");
    } finally { setBusy(false); }
  };

  const revert = async () => {
    if (!window.confirm(
      "Revert this policy to its shipped defaults? Your customisations will be lost."
    )) return;
    setBusy(true);
    try {
      await api.delete(`/admin/legal/policies/${slug}`);
      onReverted?.();
    } catch (e) {
      onError?.(formatError(e) || "Could not revert.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-legal-editor">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button className="btn-ghost" onClick={onBack} data-testid="admin-legal-back">
            ← Back to all policies
          </button>
          <h1 className="font-display font-black text-3xl mt-2">Edit: {staticDef?.title}</h1>
          <p className="text-muted text-sm mt-1">
            <code>/legal/{slug}</code> · {currentOverride
              ? "Custom version published"
              : "Currently using shipped defaults"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {currentOverride && (
            <button className="btn-ghost text-red-600 hover:text-red-700" onClick={revert}
                    disabled={busy} data-testid="admin-legal-revert">
              <Undo2 size={12}/> Revert to defaults
            </button>
          )}
          <Link className="btn-ghost" to={`/legal/${slug}`} target="_blank" rel="noopener noreferrer"
                data-testid="admin-legal-preview">
            <Eye size={12}/> Preview
          </Link>
          <button className="btn-primary" onClick={save} disabled={busy}
                  data-testid="admin-legal-save">
            {busy ? <><Loader2 size={12} className="animate-spin"/> Saving…</>
                  : <><Save size={12}/> Save & publish</>}
          </button>
        </div>
      </header>

      {/* Head fields */}
      <div className="card card-pad space-y-3">
        <div>
          <label className="eyebrow">Title</label>
          <input className="input" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="admin-legal-title"/>
        </div>
        <div>
          <label className="eyebrow">Eyebrow (small uppercase tag)</label>
          <input className="input" value={form.eyebrow}
                 onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
                 data-testid="admin-legal-eyebrow"/>
        </div>
        <div>
          <label className="eyebrow">Intro paragraph</label>
          <textarea className="input min-h-[100px]" value={form.intro}
                    onChange={(e) => setForm({ ...form, intro: e.target.value })}
                    data-testid="admin-legal-intro"/>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {form.sections.map((sec, i) => {
          const isCollapsed = collapsed[i] ?? i > 0;
          return (
            <div key={i} className="card" data-testid={`admin-legal-section-${i}`}>
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 border-b border-rule hover:bg-paper/60"
                onClick={() => setCollapsed((c) => ({ ...c, [i]: !isCollapsed }))}
                data-testid={`admin-legal-section-toggle-${i}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  {isCollapsed ? <ChevronRight size={14}/> : <ChevronDown size={14}/>}
                  <span className="font-medium truncate">{sec.heading || `Section ${i + 1}`}</span>
                </span>
                <span className="flex items-center gap-1 text-muted">
                  <span className="btn-ghost px-1" onClick={(e) => { e.stopPropagation(); moveSection(i, -1); }} aria-label="Move up">↑</span>
                  <span className="btn-ghost px-1" onClick={(e) => { e.stopPropagation(); moveSection(i, 1); }} aria-label="Move down">↓</span>
                  <span className="btn-ghost px-1 text-red-600" onClick={(e) => { e.stopPropagation(); removeSection(i); }} aria-label="Remove"
                        data-testid={`admin-legal-section-remove-${i}`}>
                    <Trash2 size={12}/>
                  </span>
                </span>
              </button>
              {!isCollapsed && (
                <div className="p-4 space-y-3">
                  <div>
                    <label className="eyebrow">Heading</label>
                    <input className="input" value={sec.heading}
                           onChange={(e) => setSection(i, { heading: e.target.value })}
                           data-testid={`admin-legal-section-heading-${i}`}/>
                  </div>
                  <div>
                    <label className="eyebrow">Paragraphs (separate paragraphs with a blank line)</label>
                    <textarea className="input min-h-[120px]" value={sec.paras}
                              onChange={(e) => setSection(i, { paras: e.target.value })}
                              data-testid={`admin-legal-section-paras-${i}`}
                              placeholder="One paragraph here.&#10;&#10;Another paragraph here."/>
                  </div>
                  <div>
                    <label className="eyebrow">Bullets (one per line)</label>
                    <textarea className="input min-h-[100px]" value={sec.bullets}
                              onChange={(e) => setSection(i, { bullets: e.target.value })}
                              data-testid={`admin-legal-section-bullets-${i}`}
                              placeholder="First bullet&#10;Second bullet"/>
                  </div>
                  {sec.table && (
                    <div className="text-xs text-muted">
                      Note — this section has a table ({sec.table.head?.length} columns × {sec.table.rows?.length} rows).
                      Table editing UI is on the roadmap; for now use the API directly or revert to defaults.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button className="btn-secondary" onClick={addSection}
                data-testid="admin-legal-add-section">
          <Plus size={12}/> Add section
        </button>
      </div>
    </div>
  );
}
